import { omit } from "lodash";
import isEqual from "lodash/isEqual";
import { useEffect, useRef } from "react";
import { FieldValues, UseFormReturn, useForm } from "react-hook-form";

import { useQueryStringValues, useUpdateQueryStringValues } from "@tsp-features/query/hooks";

export type UseConnectFormOptions<Values extends FieldValues> = {
  defaultValues: Values;
  middlewares?: Array<(prevValues: Values, nextValues: Values) => Values>;
  onUpdate?: (values: Values) => unknown;
};

function resetPageMiddleware<Values extends FieldValues>(
  prevValues: Partial<Values>,
  nextValues: Partial<Values>,
): Partial<Values> {
  const prevValuesWithoutPage = omit(prevValues, "page");
  const nextValuesWithoutPage = omit(nextValues, "page");
  if (!isEqual(prevValuesWithoutPage, nextValuesWithoutPage)) {
    return {
      ...nextValues,
      page: 1,
    };
  }

  return nextValues;
}

export function useConnectedWithQueryForm<Values extends FieldValues>(
  options: UseConnectFormOptions<Values>,
): UseFormReturn<Values> {
  const { defaultValues, middlewares = [] } = options;

  const updateQuery = useUpdateQueryStringValues(defaultValues);
  const values = useQueryStringValues(defaultValues);
  const ref = useRef<Values>(values);

  const form = useForm<Values>({ values, shouldUnregister: false });
  const { watch } = form;

  useEffect(() => {
    ref.current = values;
  }, [values]);

  useEffect(() => {
    const { unsubscribe } = watch((values, { type }) => {
      if (type === "change") {
        const transformedValues = [...middlewares, resetPageMiddleware].reduce((accumulator, middleware) => {
          accumulator = middleware(ref.current, accumulator) as Values;
          return accumulator;
        }, values as Values);

        if (!isEqual(ref.current, transformedValues)) {
          ref.current = transformedValues;
          updateQuery(ref.current);
        }
      }
    });

    return () => unsubscribe();
  }, [middlewares, watch, updateQuery]);

  return form;
}
